import {
  FolderGit2,
  Package,
  TestTube2,
  GitBranch,
  Container,
  Box,
  CheckCircle2,
  XCircle,
  FileText,
  AlertTriangle,
  Clock,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';
import type { RepositoryAnalysis } from '../types/analysis.types';

interface RepositoryDetailsCardProps {
  data: RepositoryAnalysis;
  groundingScore?: number;
}

export const RepositoryDetailsCard: React.FC<RepositoryDetailsCardProps> = ({
  data,
  groundingScore,
}) => {
  const details = data?.repositoryDetails;

  const hasDetails =
    details?.projectType ||
    details?.packageManagers?.length ||
    details?.testingFrameworks?.length ||
    (details?.cicdPlatform && details.cicdPlatform !== 'none') ||
    details?.capabilities?.length ||
    details?.iacTools?.length;

  const tags: { icon: React.ReactNode; value: string; style: string }[] = !details
    ? []
    : ([
        details.projectType && {
          icon: <FolderGit2 className="w-3.5 h-3.5" />,
          value: details.projectType.replace(/_/g, ' '),
          style: 'bg-teal-500 text-white',
        },
        ...(details.packageManagers ?? []).map((pm) => ({
          icon: <Package className="w-3.5 h-3.5" />,
          value: pm,
          style: 'bg-slate-100 dark:bg-slate-700 text-primary',
        })),
        ...(details.testingFrameworks ?? []).map((fw) => ({
          icon: <TestTube2 className="w-3.5 h-3.5" />,
          value: fw,
          style: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
        })),
        details.cicdPlatform &&
          details.cicdPlatform !== 'none' && {
            icon: <GitBranch className="w-3.5 h-3.5" />,
            value: details.cicdPlatform.replace(/_/g, ' '),
            style: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
          },
        ...(details.capabilities ?? []).map((cap) => ({
          icon: <Container className="w-3.5 h-3.5" />,
          value: cap,
          style: 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300',
        })),
        ...(details.iacTools ?? []).map((tool) => ({
          icon: <Box className="w-3.5 h-3.5" />,
          value: tool,
          style: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        })),
        details.runtimeDepsCount !== undefined && {
          icon: <Package className="w-3.5 h-3.5" />,
          value: `${details.runtimeDepsCount} deps`,
          style: 'bg-slate-100 dark:bg-slate-700 text-primary',
        },
      ].filter(Boolean) as { icon: React.ReactNode; value: string; style: string }[]);

  const docItems = [
    { label: 'README', present: details?.documentation?.hasReadme },
    { label: 'Changelog', present: details?.documentation?.hasChangelog },
    { label: 'Contributing', present: details?.documentation?.hasContributing },
    { label: 'API Docs', present: details?.documentation?.hasApiDocs },
  ];
  const hasDocs = docItems.some((d) => d.present !== undefined);

  const stats = [
    {
      icon: <FileText className="w-3.5 h-3.5" />,
      label: 'Files',
      value: data.filesCount || 0,
      color: 'text-blue-500',
      accent: 'border-blue-400 dark:border-blue-500',
    },
    {
      icon: <CheckCircle2 className="w-3.5 h-3.5" />,
      label: 'Checks',
      value: data.overview?.totalChecks || 0,
      color: 'text-teal-500',
      accent: 'border-teal-400 dark:border-teal-500',
    },
    {
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
      label: 'Warnings',
      value: data.overview?.warningChecks || 0,
      color: 'text-amber-500',
      accent: 'border-amber-400 dark:border-amber-500',
    },
    {
      icon: <XCircle className="w-3.5 h-3.5" />,
      label: 'Failed',
      value: data.overview?.failedChecks || 0,
      color: 'text-red-500',
      accent: 'border-red-400 dark:border-red-500',
    },
    ...(data.scanDuration && data.scanDuration > 0
      ? [
          {
            icon: <Clock className="w-3.5 h-3.5" />,
            label: 'Scan Time',
            value:
              data.scanDuration >= 60
                ? `${Math.floor(data.scanDuration / 60)}m ${data.scanDuration % 60}s`
                : `${data.scanDuration}s`,
            color: 'text-slate-400',
            accent: 'border-slate-300 dark:border-slate-600',
          },
        ]
      : []),
    ...(groundingScore !== undefined
      ? [
          {
            icon:
              groundingScore >= 0.7 ? (
                <ShieldCheck className="w-3.5 h-3.5" />
              ) : (
                <ShieldAlert className="w-3.5 h-3.5" />
              ),
            label: 'Confidence',
            value: `${Math.round(groundingScore * 100)}%`,
            color:
              groundingScore >= 0.7
                ? 'text-green-500'
                : groundingScore >= 0.3
                  ? 'text-amber-500'
                  : 'text-red-500',
            accent:
              groundingScore >= 0.7
                ? 'border-green-400 dark:border-green-500'
                : groundingScore >= 0.3
                  ? 'border-amber-400 dark:border-amber-500'
                  : 'border-red-400 dark:border-red-500',
          },
        ]
      : []),
  ];

  if (!hasDetails && !data.filesCount && !data.overview) return null;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-slate-200 dark:border-slate-700/50 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700/40">
          <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
            <FolderGit2 className="w-5 h-5 text-teal-600 dark:text-teal-400" />
          </div>
          <h2 className="text-lg font-semibold text-primary">Repository Details</h2>
        </div>

        {/* Tech tags */}
        {hasDetails && (
          <div className="px-5 pt-4 pb-3 flex flex-wrap gap-2">
            {tags.map((tag, i) => (
              <span
                key={i}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold capitalize ${tag.style}`}
              >
                {tag.icon}
                {tag.value}
              </span>
            ))}
          </div>
        )}

        {/* Docs */}
        {hasDocs && (
          <div className="px-5 pb-4 flex flex-wrap items-center gap-x-5 gap-y-1.5">
            <span className="text-xs font-semibold text-secondary uppercase tracking-wide">
              Docs
            </span>
            {docItems.map((d) => (
              <span
                key={d.label}
                className={`inline-flex items-center gap-1.5 text-sm font-medium ${
                  d.present
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-slate-300 dark:text-slate-600'
                }`}
              >
                {d.present ? (
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                ) : (
                  <XCircle className="w-3.5 h-3.5 shrink-0" />
                )}
                {d.label}
              </span>
            ))}
          </div>
        )}

        {/* Stats strip */}
        <div className="flex divide-x divide-slate-100 dark:divide-slate-700/40 border-t border-slate-100 dark:border-slate-700/40">
          {stats.map((stat) => (
            <div key={stat.label} className="flex-1 px-5 py-4">
              <div className="text-[11px] font-medium text-secondary leading-none mb-1.5">
                {stat.label}
              </div>
              <div className={`text-3xl font-black tabular-nums leading-none ${stat.color}`}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RepositoryDetailsCard;
